import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import {
  verificationFormInputArr,
  verificationFormSchema,
} from "./VerificationFormHelpers";
import { commonToast } from "../../Utils";
import { isValidEmail, LoadingButton } from "../../Utils/CustomComponent";
import FormBox from "../../components/FormBox/FormBox";
import slsLogo from "../../assets/images/loanlinq.png";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import urls from "../../Utils/urls";
import { loadingTypes, oracleConfig } from "../../Utils/constant";
import { useDispatch, useSelector } from "react-redux";
import { fetchUSStates } from "../../store/reducers/dashboardSlice/api";
import PopupWrapper from "../../components/PopupWrapper";
import {
  ChevronLeft,
  ExclamationTriangleFill,
  FileEarmarkCheck,
} from "react-bootstrap-icons";
import { Button } from "../../components/Button";

const VerificationForm = () => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showInvalidModal, setShowInvalidModal] = useState(false);
  const [invalidModalMessage, setInvalidModalMessage] = useState("");
  const states = useSelector((state) => state.dashboard.USstates.data);
  const status = useSelector((state) => state.dashboard.USstates.status);
  const { isLargeDevice } = useSelector((state) => state.RS_Sidebar);
  const { email } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(verificationFormSchema),
    defaultValues: {
      to_name:
        "Superior Loan Servicing Inc. \n28348 Roadside Dr. 1st Floor\nAgoura Hills, CA 91301",
      // to_address: "28348 Roadside Dr. 1st Floor\nAgoura Hills, CA 91301",
      to_street: "28348 Roadside Dr. 1st Floor",
      to_city: "Agoura Hills",
      to_state: "CA",
      to_zipcode: "91301",
      lender_date: new Date(),
      borrower_sign_available: false,
      lender_title: "Lender",
    },
  });

  const display = watch("borrower_sign_available");
  const borrowerSignature = watch("borrower_signature");
  const requestDate = new Date();

  useEffect(() => {
    if (status === loadingTypes.idle) {
      dispatch(fetchUSStates());
    }
  }, [dispatch, status]);

  const formSections = verificationFormInputArr(
    display,
    states,
    errors?.[""]?.message
  );

  if (!email || !isValidEmail(email)) {
    return <Navigate to="unauthorized" replace />;
  }

  const onSubmit = async (data) => {
    setSubmitLoading(true);
    const id = toast.loading("Submitting Verification Form...");

    try {
      const trimmedData = Object.fromEntries(
        Object.entries(data).map(([k, v]) => [
          k,
          typeof v === "string" ? v.trim() : v,
        ])
      );
      const payload = {
        ...trimmedData,
        to_name: "Superior Loan Servicing Inc.",
        to_address: "28348 Roadside Dr. 1st Floor - Agoura Hills, CA 91301",
        to_street: "28348 Roadside Dr. 1st Floor",
        to_city: "Agoura Hills",
        to_state: "CA",
        to_zipcode: "91301",
        email,
        borrower_signature: borrowerSignature?.base64,
        lender_date: moment(requestDate).toISOString(),
        borrower_sign_available: trimmedData?.borrower_sign_available
          ? "Y"
          : "N",
      };

      const res = await axios.post(urls.VOM.submitVOM, payload, oracleConfig);
      if (res?.data?.status === "success") {
        toast.update(id, {
          render: "Verification Form Submitted Successfully",
          type: "success",
          isLoading: false,
          ...commonToast,
        });

        navigate("/thankyou", {
          state: {
            text: "Your VOM request has been successfully submitted.",
            showBtn: false,
          },
        });
      } else {
        const parsedRes = JSON?.parse(res?.data?.response);

        if (parsedRes?.message?.includes("Property address not found.")) {
          setShowInvalidModal(true);
          setInvalidModalMessage(
            "Invalid property address! Please enter a valid property address."
          );
          toast.dismiss(id);

          return;
        } else if (parsedRes?.message?.includes("Loan Number not found.")) {
          setShowInvalidModal(true);
          setInvalidModalMessage(
            "Invalid loan account number! Please enter a valid loan account number."
          );
          toast.dismiss(id);
          return;
        }
        toast.update(id, {
          render: "Verification Form Submitted Successfully",
          type: "success",
          isLoading: false,
          ...commonToast,
        });

        navigate("/thankyou", {
          state: {
            text: "Your VOM request has been successfully submitted.",
            showBtn: false,
          },
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.update(id, {
        render: "Submission Failed. Please try again.",
        type: "error",
        isLoading: false,
        ...commonToast,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100" style={{ backgroundColor: "#f5f5f5" }}>
      {/* FIXED TOP NAVBAR */}
      <VOMNavbar />

      {/* Content */}
      <div style={{ paddingTop: "80px" }}>
        <div className={`container pb-5 ${isLargeDevice ? "w-100" : "w-100"}`}>
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item d-flex align-items-center">
                <ChevronLeft size={15} className="" />
                <span
                  className="linkClick text-dcoration-none"
                  role="button"
                  onClick={() => navigate(-1)}
                >
                  Back
                </span>
              </li>
            </ol>
          </nav>
          {/* Form Card */}
          <div className="card shadow-lg rounded border-0 mt-4 pb-5 px-0 pt-0">
            <div className="card-body pb-5 px-0 pt-0">
              {/* Title */}

              <h3 className="fw-bold text-uppercase mb-2 text-white bg-primary p-5 rounded">
                Request for Verification of Rent or Mortgage
              </h3>

              <p className="text-muted mb-5">
                We have received an application for a loan from the applicant
                listed below, to whom we understand you rent or have extended a
                loan.
              </p>

              {/* Instructions */}
              {/* <div className="px-4 py-4 border-bottom border-top border-2 border-dark bg-light my-5 text-center">
                <p className="mb-1 small">
                  <strong>INSTRUCTIONS:</strong> <strong>LENDER-</strong>{" "}
                  Complete items 1 thru 8. Have applicant(s) complete item 9.
                  Forward directly to lender named in item 1.
                </p>
                <p className="mb-0 small">
                  <strong>LANDLORD/CREDITOR-</strong> Please complete Part II as
                  applicable. Sign and return directly to the lender named in
                  item 2.
                </p>
              </div> */}

              <form onSubmit={handleSubmit(onSubmit)} className="px-5">
                {/* Part I Header */}
                <p className="text-muted">
                  <span className="fw-bold">Request Date: </span>{" "}
                  <span>{moment(requestDate)?.format("MM-DD-YYYY")}</span>
                </p>
                <div className="bg-primary text-white text-center p-4 fw-bold">
                  Lender Details
                </div>

                {formSections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className={`${section.className}`}>
                    {section.arr.map((subsection, subsectionIndex) => (
                      <div
                        key={subsectionIndex}
                        className={` p-3 ${subsection.className}`}
                      >
                        <FormBox
                          options={subsection.arr}
                          register={register}
                          watch={watch}
                          setValue={setValue}
                          errors={errors}
                        />
                      </div>
                    ))}
                  </div>
                ))}

                {/* Part II Section */}
                {/* <div className="">
                  <div className="bg-secondary text-white text-center py-2 fw-bold">
                    PART II - TO BE COMPLETED BY LANDLORD/CREDITOR
                  </div>
                  <div className="p-4 text-center text-muted">
                    <p className="mb-0">
                      <em>
                        This section is to be completed by the Landlord/Creditor
                        and returned to the lender.
                      </em>
                    </p>
                  </div>
                </div> */}

                {/* Submit Button */}
                <div className="p-4 bg-light">
                  <LoadingButton
                    type="submit"
                    className="btn btn-primary btn-lg px-5 my-5"
                    title="Submit Verification Form"
                    disabled={submitLoading}
                    loading={submitLoading}
                  />
                </div>
              </form>

              {/* Footer Note */}
              <div className="px-5 py-3 bg-warning bg-opacity-10 border-top border-2 border-dark">
                <p className="mb-0 small">
                  <strong>Note:</strong> The confidentiality of the information
                  you have furnished will be preserved except where disclosure
                  of this information is required by applicable law. The form is
                  to be transmitted directly to the lender and is not to be
                  transmitted through the applicant or any other party.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <InvalidModal
        show={showInvalidModal}
        setShow={setShowInvalidModal}
        message={invalidModalMessage}
      />
    </div>
  );
};

export default VerificationForm;

export const VOMNavbar = ({ title = "VOM Request" }) => {
  return (
    <nav className="navbar navbar-light bg-white fixed-top px-5 py-3 shadow-sm">
      {/* Logo */}
      <a className="navbar-brand d-flex align-items-center gap-2" href="#">
        <img
          src={slsLogo}
          alt="LOANLINQ"
          height="40"
          className="d-inline-block"
          style={{ objectFit: "contain" }}
        />
      </a>
      <div className="text-muted d-flex gap-2 fw-bold px-5">
        <div className="d-flex align-items-center">
          <FileEarmarkCheck size={18} />
        </div>
        <div>{title}</div>
      </div>
    </nav>
  );
};

const InvalidModal = ({ show, setShow, message }) => {
  return (
    <PopupWrapper
      title={
        <h2 className="p-4 font-weight-bold text-error">
          {" "}
          <ExclamationTriangleFill
            className="mt-2 me-2"
            color="#dc3545"
            size={25}
          />
        </h2>
      }
      extraClass="width-800"
      Component={
        <div>
          <p className="text-error">{message}</p>
          <div className="text-end">
            <Button text={"OK"} onClick={() => setShow(false)} />
          </div>
        </div>
      }
      toShow={show}
      closeEventHandler={() => setShow(false)}
      backdrop="static"
    />
  );
};
